import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, delay, finalize, map, merge, Observable, of, Subscription, switchMap } from 'rxjs';
import { computed, Component, DestroyRef, inject, OnInit, output, Signal, signal, WritableSignal } from '@angular/core';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { GoogleMap, MapMarker } from '@angular/google-maps';

/**
 * interfaces
 */
import {
    IUnitFormControls,
    ICreateUnitPayload,
    IUpdateUnitPayload,
} from '../../interfaces';

/**
 * enums
 */
import { OperationModifyMode } from '@core/enums/operation-mode';

/**
 * consts
 */
import { UnitsTranslateMapper } from '../../consts';

/**
 * models
*/
import { IUnitModel, IUnitChildModel } from '../../models';

/**
 * services
*/
import { AppHttpErrorResponse } from '@core/network';
import { UnitApiService, UnitService, UnitFormService } from '../../services';

/**
 * core
 */
import { LookupService } from '@core/services/lookup/lookup.service';

/**
 * shared
 */
import { TextareaComponent } from '@forms/components/textarea/textarea.component';
import { InputFieldComponent } from '@forms/components/input-field/input-field.component';
import { FormFieldFeedbackDirective } from "@forms/directives/form-field-feedback/form-field-feedback.directive";

/**
 * Standalone form component for creating and editing a unit.
 * Used inside a modal — call {@link initCreate} or {@link initEdit} before showing.
 *
 * Providers are declared here so each modal instance gets its own service scope.
 */
@Component({
    exportAs: 'unitFormApi',
    selector: 'app-unit-form',
    templateUrl: './unit-form.component.html',
    styleUrl: './unit-form.component.scss',
    imports: [
        ReactiveFormsModule,
        /**
         * libs
         */
        GoogleMap,
        MapMarker,
        TranslatePipe,
        NgSelectComponent,
        /**
         * shared
         */
        TextareaComponent,
        InputFieldComponent,
        FormFieldFeedbackDirective,
    ],
    providers: [
        UnitApiService,
        UnitService,
        UnitFormService,
    ],
})
export class UnitFormComponent implements OnInit {

    // ─── Services ─────────────────────────────────────────────────────────────

    /** @private Unit HTTP service. */
    private readonly _unitService   = inject(UnitService);

    /** @private Lookup service for populating unit-type dropdown options. */
    private readonly _lookupService = inject(LookupService);

    /** @private Manages form state, validation, and payload construction. */
    private readonly _formService   = inject(UnitFormService);

    /** @private Destroy reference for RxJS stream cleanup. */
    private readonly _destroyRef    = inject(DestroyRef);

    // ─── Outputs ──────────────────────────────────────────────────────────────

    /** Emits the created / updated unit ID on successful submit. */
    public readonly submitted = output<string>();

    // ─── Public state ─────────────────────────────────────────────────────────

    /** Translate key mapper exposed to the template. */
    public readonly TRANSLATE_KEY_MAP = UnitsTranslateMapper;

    /** True while an API call is in flight. */
    public readonly isLoading: Signal<boolean> = this._formService.isLoading;

    /** True when the form is in create mode. */
    public readonly isCreationMode: Signal<boolean> = this._formService.isCreationMode;

    /** The reactive form group. */
    public get unitForm(): FormGroup<IUnitFormControls> {
        return this._formService.form;
    }

    /** All unit types from the lookup stream, used as the unfiltered source. */
    private readonly _allUnitTypes = toSignal(
        this._lookupService.unitTypesLookup$,
        { initialValue: [] }
    );

    /** Unit types filtered to match the target level of the unit being created / edited. */
    public readonly filteredUnitTypes = computed(() => {
        const level = this._formService.targetLevel();
        return this._allUnitTypes().filter((t) => t.value === level);
    });

    /** The unit that gives context to the form (parent on create, self on edit). */
    public readonly relatedUnit = this._formService.relatedUnit;

    /** The level the new / edited unit will occupy in the hierarchy. */
    public readonly targetLevel = this._formService.targetLevel;

    /** Active subscription to the getById or create/update API call; cancelled on {@link cancel}. */
    private _apiSubscription!: Subscription;

    /**
     * frazzled state
     * @description Returns true if loading is true or form is invalid
     */
    public get frazzled(): boolean {
        return  this.isLoading() || this.unitForm.invalid
    }

    // ─── Map state ────────────────────────────────────────────────────────────

    /** Writable signal that drives the Google Map `center` binding. */
    public readonly mapCenter: WritableSignal<google.maps.LatLngLiteral> = signal({ lat: 0, lng: 0 });

    /** Writable signal that drives the draggable map marker position. */
    public readonly mapMarker: WritableSignal<google.maps.LatLngLiteral>  = signal({ lat: 0, lng: 0 });

    /** Shared Google Maps display options (zoom / type controls hidden). */
    public readonly mapOptions: google.maps.MapOptions = {
        fullscreenControl: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: false,
        draggable: true,
    };

    constructor() {}

    /** @inheritdoc */
    public ngOnInit(): void {
        this._watchCoordinates();
    }

    // ─── Lifecycle helpers ────────────────────────────────────────────────────

    /**
     * Prepares the form for creating a new unit.
     * @param parentUnit - The tree node that will be the parent, or null for root-level adds.
     */
    public initCreate(parentUnit: IUnitModel | IUnitChildModel | null): void {
        this._formService.setOperationMode(OperationModifyMode.Create);
        this._formService.setSelectedId(null);
        this._formService.resetForm();
        this._formService.setRelatedUnit(parentUnit);
        this.syncMap(parentUnit?.latitude ?? '', parentUnit?.longitude ?? '');
    }

    /**
     * Fetches the unit by ID and prepares the form for editing.
     * @param id - ID of the unit to edit.
     */
    public initEdit(unit: IUnitModel | IUnitChildModel): void {
        this._formService.setOperationMode(OperationModifyMode.Update);
        this._formService.setSelectedId(unit.id);
        this._formService.setRelatedUnit(unit);
        this._formService.setLoading(true);
        this._formService.form.disable()

        const subscription  = this._unitService
                        .getById(unit.id)
                        .pipe(
                            finalize(() => {
                                this._formService.setLoading(false)
                                this._formService.form.enable()
                        }))
                        .subscribe((model) => {
                            this._formService.setRelatedUnit(model);
                            this._formService.patchValue(model);
                            this.syncMap(model.latitude, model.longitude);
                        });

        this._apiSubscription = subscription;
    }

    /** Syncs the map center and marker to the given coordinate strings. */
    public syncMap(latStr: string, lngStr: string): void {
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        const position = isNaN(lat) || isNaN(lng) ? { lat: 0, lng: 0 } : { lat, lng };
        this.mapCenter.set(position);
        this.mapMarker.set(position);
    }

    // ─── Map event handlers ───────────────────────────────────────────────────

    /** Handles a click on the map surface; moves marker to the clicked position. */
    public onMapClick(event: google.maps.MapMouseEvent): void {
        this._applyMapPosition(event);
    }

    /** Handles the end of a marker drag; updates coordinates to the dropped position. */
    public onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
        this._applyMapPosition(event);
    }

    // ─── Actions ──────────────────────────────────────────────────────────────

    /**
     * Validates the form and triggers the create or update API call.
     * Marks all controls as touched to reveal validation errors on invalid submit.
     */
    public submit(): void {

        if (this.unitForm.invalid) {
            this.unitForm.markAllAsTouched();
            return;
        }

        this._formService.form.disable();
        this._formService.setLoading(true);

        this._operationCallbackApi()
            .pipe(finalize(() => {
                this._formService.setLoading(false)
            }))
            .subscribe({
                next: (result) => {
                this._formService.form.enable();
                    this._formService.resetForm();
                    this.submitted.emit(result);
                },
                error: (err: AppHttpErrorResponse) => {
                    this._formService.form.enable();
                    err.handleControlServerSideValidationErrors(this.unitForm);
                }
            });
    }

    /** Resets form state without emitting any event. */
    public cancel(): void {

        // reset form
        this._formService.resetForm();

        // unsubscribe
        if (this._apiSubscription != null) {
            this._apiSubscription.unsubscribe()
        }
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    /** Reads the current latitude / longitude form values and updates the map marker (and optionally the center). */
    private _syncMapFromForm(centerUpdate: boolean = true): void {
        const { lat: latCtrl, lng: lngCtrl } = this._getLatLngFormControl();
        const lat = parseFloat(latCtrl.value ?? '');
        const lng = parseFloat(lngCtrl.value ?? '');
        if (isNaN(lat) && isNaN(lng)) return;
        const position = { lat, lng };
        if (centerUpdate) this.mapCenter.set(position);
        this.mapMarker.set(position);
    }

    /** Subscribes to latitude / longitude value changes and keeps the map in sync. */
    private _watchCoordinates(): void {
        const { lat: latCtrl, lng: lngCtrl } = this._getLatLngFormControl();
        merge(latCtrl.valueChanges, lngCtrl.valueChanges)
            .pipe(
                delay(100),
                map(() => !(latCtrl.pristine && lngCtrl.pristine)),
                switchMap(status => of(status).pipe(debounceTime(status ? 0 : 500))),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(centerUpdate => this._syncMapFromForm(centerUpdate));
    }

    /** Returns the latitude and longitude `FormControl` instances from the reactive form. */
    private _getLatLngFormControl(): { lat: FormControl; lng: FormControl } {
        return {
            lat: this.unitForm.controls['latitude'] as FormControl,
            lng: this.unitForm.controls['longitude'] as FormControl,
        };
    }

    /** Returns the correct API observable (create or update) based on the current operation mode. */
    private _operationCallbackApi(): Observable<string> {

        if (this.isCreationMode()) {
            const payload: ICreateUnitPayload = this._formService.buildCreatePayload();
            return this._unitService.create(payload);
        }

        const id = this._formService.selectedId();
        if (id == null) throw new Error('Unit ID is required for update');

        const payload: IUpdateUnitPayload = this._formService.buildUpdatePayload();
        return this._unitService.update(id, payload);
    }

    /** Extracts `lat` / `lng` from a map event and patches the form and marker. */
    private _applyMapPosition(event: google.maps.MapMouseEvent): void {
        if (event.latLng == null) return;

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        this.unitForm.patchValue({ latitude: lat.toString(), longitude: lng.toString() });
        this.unitForm.markAsDirty();

        const { lat: latCtrl, lng: lngCtrl } = this._getLatLngFormControl();
        latCtrl.markAsPristine();
        lngCtrl.markAsPristine();

        this.mapMarker.set({ lat, lng });
    }

}
